// CallLogsPage.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { AstrologerCallLogs, AstrologerPerformanceSummary } from '../../services/psychic.services';
import { useParams } from 'react-router-dom';
import { formatSecondstoHHMMSS } from '../../../../utils/commonfunction';
import CustomPagination from '../../common/custompagination';

export default function CallLogsPage() {
  const params = useParams();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [datalength, setDatalength] = useState(0);
  const [page, setPage] = useState(1);
  const item_perpage = 10;

  // Filters State
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    consultation_type: '',
    from_date: '',
    to_date: ''
  });

  useEffect(() => {
    fetchSummary();
  }, [params.id]);

  useEffect(() => {
    fetchLogs();
  }, [params.id, page, filters]);

  const fetchSummary = async () => {
    const res = await AstrologerPerformanceSummary(params.id);
    setSummary(res.data);
  };

  const fetchLogs = async () => {
    const payload = {
      astrologer_id: params.id,
      page,
      item_perpage,
      search: filters.search || undefined,
      status: filters.status || undefined,
      consultation_type: filters.consultation_type || undefined,
      from_date: filters.from_date || undefined,
      to_date: filters.to_date || undefined,
    };

    const res = await AstrologerCallLogs(payload);
    setLogs(res.data.logs || []);
    setDatalength(res.data.total_records || 0);
  };

  const handleReset = () => {
    setFilters({
      search: '',
      status: '',
      consultation_type: '',
      from_date: '',
      to_date: ''
    });
    setPage(1);
  };

  return (
    <div className="container-fluid py-4">

      {/* Performance Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h5>Total Requests</h5>
              <h2>{summary.total || 0}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h5>Accepted</h5>
              <h2>{summary.accepted || 0} ({summary.rejectionRate ? (100 - summary.rejectionRate).toFixed(1) : 0}%)</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`card ${summary.rejectionRate > 20 ? 'bg-danger' : 'bg-warning'} text-white`}>
            <div className="card-body">
              <h5>Rejected</h5>
              <h2>{summary.rejected || 0} ({summary.rejectionRate?.toFixed(1) || 0}%)</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h5>Avg Call Time</h5>
              <h2>{summary.averageCallTime ? formatSecondstoHHMMSS(summary.averageCallTime) : '—'}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header bg-light">
          <h5 className="mb-0">Filters</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* Search */}
            <div className="col-md-3">
              <input
                type="text"
                className="form-control cp"
                placeholder="Search user name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>

            {/* Status */}
            <div className="col-md-2">
              <select
                className="form-select cp"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Consultation Type */}
            <div className="col-md-2">
              <select
                className="form-select cp"
                value={filters.consultation_type}
                onChange={(e) => setFilters({ ...filters, consultation_type: e.target.value })}
              >
                <option value="">All Types</option>
                <option value="chat">Chat</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
              </select>
            </div>

            {/* From Date */}
            <div className="col-md-2">
              <input
                type="date"
                className="form-control cp"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
              />
            </div>

            {/* To Date */}
            <div className="col-md-2">
              <input
                type="date"
                className="form-control cp"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
              />
            </div>

            {/* Reset Button */}
            <div className="col-md-1">
              <button className="btn btn-warning w-100 cp" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4>Call Request Logs</h4>
          <span className="text-muted">Total: {datalength}</span>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Date & Time</th>
                  <th>Response Time</th>
                  <th>Status</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      No logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log, i) => (
                    <tr key={log._id}>
                      <td>{(page - 1) * item_perpage + i + 1}</td>
                      <td>{log.user_name || 'Unknown User'}</td>
                      <td>
                        <span className={`badge ${
                          log.mode_of_consultation === 'call' ? 'bg-primary' :
                          log.mode_of_consultation === 'video' ? 'bg-info' :
                          log.mode_of_consultation === 'chat' ? 'bg-success' : 'bg-warning'
                        }`}>
                          {log.mode_of_consultation || 'N/A'}
                        </span>
                      </td>
                      <td>{new Date(log.request_timestamp).toLocaleString()}</td>
                      <td>
                        {log.response_timestamp
                          ? ((new Date(log.response_timestamp) - new Date(log.request_timestamp)) / 1000).toFixed(1) + 's'
                          : '—'}
                      </td>
                      <td>
                        <span className={`badge ${
                          log.status === 'accepted' ? 'bg-success' :
                          log.status === 'rejected' ? 'bg-danger' : 'bg-warning'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                      <td>
                        {log.duration_in_seconds
                          ? formatSecondstoHHMMSS(log.duration_in_seconds)
                          : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {datalength > 0 && (
            <CustomPagination
              datalength={datalength}
              itemperpage={item_perpage}
              currentPage={page}
              setPage={setPage}
              pageRoute={[{
                name: "Astrologer Call Logs",
                path: `/admin/astrologer-management/astrologer/call-logs/${params.id}`
              }]}
            />
          )}
        </div>
      </div>
    </div>
  );
}